/**
 * Design-system button primitives: CTA, Action, Subtle, Text, Icon, Expand,
 * React, BreadCrumb, plus Tag variants.
 *
 * All buttons in the design system are composed on top of `<HoverView>` from
 * `basics.tsx`, which is the single source of truth for press/hover/disabled
 * state. Variants differ in visual weight, icon placement, and text renderer,
 * but their interaction behavior is identical. When you need a new variant,
 * extend `<HoverView>` or compose with it; don't reimplement press handling.
 * `<Tag>` is the one presentational exception (no press handler);
 * `<ClickThroughTag>` wraps `<CTAButton>` when a tag needs to behave as a
 * button.
 */

import { toHoverColor, toPressColor, translucentHoverColor, translucentPressColor, useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import React, { useState } from 'react';
import type { DimensionValue } from 'react-native';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import type { FormatParams, TLabel } from 'util/stdtypes';
import { makeTestID, t } from 'util/stdtypes';

import { HoverView, Pad, PadBox } from './basics';
import { useFormDisabled } from './form/form-disabled';
import type { TextType, TextWeight } from './text';
import { Heading, Paragraph, UtilityText } from './text';
import { useTranslation } from './translation';
export type ButtonType = 'primary' | 'secondary' | 'accent' | 'disabled' | 'delete';
export type ButtonSize = 'large' | 'small';

export type CTAButtonProps = {
    label?: TLabel;
    text?: string;
    testID?: string;
    formatParams?: FormatParams;
    leftIcon?: React.ReactNode;
    icon?: React.ReactNode;
    type?: ButtonType;
    disabled?: boolean;
    size?: ButtonSize;
    wide?: boolean;
    borderless?: boolean;
    inProgress?: boolean;
    onPress?: () => void;
};

export function CTAButton({
    label,
    text,
    testID,
    formatParams,
    leftIcon,
    icon,
    type = 'primary',
    disabled: ownDisabled = false,
    size = 'large',
    wide = false,
    borderless = false,
    inProgress = false,
    onPress,
}: CTAButtonProps) {
    const formDisabled = useFormDisabled();
    const disabled = formDisabled || ownDisabled;
    const { colors } = useTheme();
    const s = useCTAButtonStyles();

    const styleMap = {
        primary: { normal: s.primary, hover: s.primaryHover, pressed: s.primaryPressed, textColor: colors.colorTextOnPrimaryButton },
        secondary: { normal: s.secondary, hover: s.secondaryHover, pressed: s.secondaryPressed, textColor: colors.colorTextPrimary },
        accent: { normal: s.accent, hover: s.accentHover, pressed: s.accentPressed, textColor: colors.colorTextOnAccentColorBg },
        disabled: { normal: s.disabled, hover: s.disabled, pressed: s.disabled, textColor: colors.colorTextDisabled },
        delete: { normal: s.delete, hover: s.deleteHover, pressed: s.deletePressed, textColor: colors.colorTextWarning },
    };
    const { normal, hover, pressed, textColor } = styleMap[disabled ? 'disabled' : type] ?? styleMap.primary;

    const sizeMap = {
        large: s.largeButton,
        small: s.smallButton,
    };

    const progressColor = type === 'secondary' || type === 'delete' ? colors.colorButtonAccent : textColor;

    return (
        <HoverView
            disabled={disabled || inProgress}
            hoverStyle={[s.hover, hover]}
            onPress={onPress}
            pressedStyle={pressed}
            role='button'
            style={[sizeMap[size], s.base, wide && s.wide, normal, borderless && s.borderless]}
            testID={makeTestID({ testID, label, text, formatParams })}
        >
            {leftIcon}
            {!inProgress && (
                <UtilityText color={textColor} formatParams={formatParams} label={label} numberOfLines={1} text={text} type='large' />
            )}
            {inProgress && <ActivityIndicator color={progressColor} size='small' style={s.activityIndicator} />}
            {icon}
        </HoverView>
    );
}

const useCTAButtonStyles = createThemedStyles((tokens) => ({
    activityIndicator: {},
    base: {
        maxWidth: '100%',
        alignItems: 'center',
        alignSelf: 'flex-start',
        borderRadius: 32,
        borderWidth: 1,
        flexDirection: 'row',
    },
    hover: {},
    primary: {
        backgroundColor: tokens.colors.colorButtonPrimary,
        borderColor: tokens.colors.colorButtonPrimary,
    },
    primaryHover: {
        backgroundColor: toHoverColor(tokens.colors.colorButtonPrimary),
        borderColor: toHoverColor(tokens.colors.colorButtonPrimary),
    },
    primaryPressed: {
        backgroundColor: toPressColor(tokens.colors.colorButtonPrimary),
        borderColor: toPressColor(tokens.colors.colorButtonPrimary),
    },
    secondary: {
        borderColor: tokens.colors.colorBorderPrimary,
        backgroundColor: tokens.colors.colorButtonSecondary,
    },
    secondaryPressed: {
        backgroundColor: toPressColor(tokens.colors.colorButtonSecondary),
    },
    secondaryHover: {
        backgroundColor: toHoverColor(tokens.colors.colorButtonSecondary),
    },
    accent: {
        backgroundColor: tokens.colors.colorButtonAccent,
        borderColor: tokens.colors.colorButtonAccent,
    },
    accentPressed: {
        backgroundColor: toPressColor(tokens.colors.colorButtonAccent),
        borderColor: toPressColor(tokens.colors.colorButtonAccent),
    },
    accentHover: {
        backgroundColor: toHoverColor(tokens.colors.colorButtonAccent),
        borderColor: toHoverColor(tokens.colors.colorButtonAccent),
    },
    delete: {
        borderColor: tokens.colors.colorBorderPrimary,
    },
    deletePressed: {
        backgroundColor: toPressColor(tokens.colors.colorSurfacePrimary),
        borderColor: tokens.colors.colorBorderPrimary,
    },
    deleteHover: {
        backgroundColor: toHoverColor(tokens.colors.colorSurfacePrimary),
        borderColor: tokens.colors.colorBorderPrimary,
    },
    disabled: {
        backgroundColor: tokens.colors.colorButtonDisabled,
        borderColor: tokens.colors.colorButtonDisabled,
        color: tokens.colors.colorTextDisabled,
    },
    largeButton: {
        height: 44,
        paddingHorizontal: 20,
        gap: 12,
    },
    smallButton: {
        height: 40,
        paddingHorizontal: 20,
        paddingVertical: 8,
        gap: 12,
    },
    wide: {
        alignSelf: 'stretch',
        justifyContent: 'center',
    },
    borderless: {
        borderWidth: 0,
    },
}));

export type ClickThroughTagProps = { label?: TLabel; text?: string; emoji?: string; onPress?: () => void };

export function ClickThroughTag({ label, text, emoji, onPress }: ClickThroughTagProps) {
    return (
        <CTAButton
            borderless
            label={label}
            leftIcon={emoji ? <UtilityText text={emoji} type='small' weight='medium' /> : undefined}
            onPress={onPress}
            size='small'
            text={text}
            type='secondary'
        />
    );
}

export type IconButtonProps = {
    icon?: React.ComponentType<any>;
    onPress?: () => void;
    iconProps?: Record<string, unknown>;
    testID?: string;
};

export function IconButton({ icon, onPress, iconProps = {}, testID }: IconButtonProps) {
    const s = useIconButtonStyles();
    return (
        <HoverView hoverStyle={s.hover} onPress={onPress} pressedStyle={s.pressed} role='button' style={s.button} testID={testID}>
            {icon && React.createElement(icon, iconProps)}
        </HoverView>
    );
}
const useIconButtonStyles = createThemedStyles((tokens) => ({
    button: {
        color: tokens.colors.colorTextPrimary,
        height: 32,
        width: 32,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 16,
        borderWidth: 1,
        borderColor: tokens.colors.colorBorderPrimary,
        backgroundColor: tokens.colors.colorSurfacePrimary,
    },
    hover: {
        backgroundColor: toHoverColor(tokens.colors.colorSurfacePrimary),
    },
    pressed: {
        backgroundColor: toPressColor(tokens.colors.colorSurfacePrimary),
    },
}));

export type ActionButtonProps = {
    label?: TLabel;
    text?: string;
    testID?: string;
    ariaLabel?: TLabel;
    icon?: React.ComponentType<any>;
    iconProps?: Record<string, unknown>;
    wide?: boolean;
    onPress?: () => void;
    textColor?: string;
    hover?: boolean;
    pressed?: boolean;
    disabled?: boolean;
};

export function ActionButton({
    label,
    text,
    testID,
    ariaLabel,
    icon,
    iconProps = {},
    wide = false,
    onPress,
    textColor,
    hover = false,
    pressed = false,
    disabled: ownDisabled = false,
}: ActionButtonProps) {
    const formDisabled = useFormDisabled();
    const disabled = formDisabled || ownDisabled;
    const { colors } = useTheme();
    const s = useActionButtonStyles();
    const iconColorOverride = 'color' in iconProps ? iconProps.color : undefined;
    const iconColor = disabled ? colors.colorTextDisabled : iconColorOverride;
    return (
        <HoverView
            ariaLabel={ariaLabel}
            disabled={disabled}
            hoverStyle={[s.hover]}
            onPress={onPress}
            pressedStyle={s.pressed}
            role='button'
            style={[s.button, wide ? s.wide : null, hover ? s.hover : null]}
            testID={makeTestID({ testID, ariaLabel, label, text })}
        >
            {icon && React.createElement(icon, { ...iconProps, color: iconColor })}
            {icon && (label || text) && <Pad size={8} />}
            <UtilityText color={disabled ? colors.colorTextDisabled : textColor} label={label} text={text} />
        </HoverView>
    );
}
const useActionButtonStyles = createThemedStyles((tokens) => ({
    button: {
        flexDirection: 'row',
        height: 32,
        alignItems: 'center',
        paddingHorizontal: 8,
        alignSelf: 'flex-start',
        borderRadius: 32,
        color: tokens.colors.colorTextPrimary,
        backgroundColor: tokens.colors.colorSurfacePrimary,
    },
    wide: {
        alignSelf: 'stretch',
        justifyContent: 'center',
    },
    hover: {
        backgroundColor: toHoverColor(tokens.colors.colorSurfacePrimary),
    },
    pressed: {
        backgroundColor: toPressColor(tokens.colors.colorSurfacePrimary),
    },
    disabled: {
        color: tokens.colors.colorTextDisabled,
    },
}));

export type SubtleButtonProps = {
    ariaHidden?: boolean;
    label?: TLabel;
    text?: string;
    ariaLabel?: TLabel;
    ariaText?: string;
    testID?: string;
    disabled?: boolean;
    formatParams?: FormatParams;
    color?: string;
    icon?: React.ComponentType<any>;
    iconProps?: Partial<Record<string, unknown>> & { after?: boolean };
    padLeft?: boolean;
    padRight?: boolean;
    onPress?: () => void;
    tabIndex?: 0 | -1;
    hover?: boolean;
    emphasized?: boolean;
};

export function SubtleButton({
    ariaHidden,
    label,
    text,
    ariaLabel,
    ariaText,
    testID,
    disabled: ownDisabled = false,
    formatParams,
    color,
    icon,
    iconProps,
    padLeft = false,
    padRight = false,
    onPress,
    tabIndex,
    hover: externalHover,
    emphasized = false,
}: SubtleButtonProps) {
    const formDisabled = useFormDisabled();
    const disabled = formDisabled || ownDisabled;
    const { colors } = useTheme();
    const resolvedColor = color ?? colors.colorTextSecondary;
    const s = SubtleButtonStyle;
    const [internalHover, setInternalHover] = useState(false);
    const hover = externalHover ?? internalHover;
    const { after, ...iconRest } = (iconProps as any) || {};
    const effectiveColor = disabled ? colors.colorTextDisabled : emphasized ? colors.colorTextAccent : resolvedColor;

    const iconElement = icon ? React.createElement(icon, { ...iconRest, color: effectiveColor } as any) : null;
    const isIconAfter = !!after;

    return (
        <HoverView
            ariaHidden={ariaHidden}
            ariaLabel={ariaLabel}
            ariaText={ariaText}
            disabled={disabled}
            onPress={onPress}
            role={!disabled ? 'button' : undefined}
            setHover={setInternalHover}
            style={[s.button, padLeft && { marginLeft: 20 }, padRight && { marginRight: 20 }]}
            tabIndex={tabIndex}
            testID={makeTestID({ testID, ariaLabel, label, text, formatParams })}
        >
            {isIconAfter ? null : iconElement}
            {isIconAfter ? null : icon && <Pad size={4} />}
            <UtilityText
                color={effectiveColor}
                formatParams={formatParams}
                label={label}
                text={text}
                type='small'
                underline={hover && !disabled}
                weight='medium'
            />
            {isIconAfter && <Pad size={4} />}
            {isIconAfter ? iconElement : null}
        </HoverView>
    );
}

const SubtleButtonStyle = StyleSheet.create({
    button: {
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'flex-start',
    },
});

export type TextButtonProps = {
    label?: TLabel;
    text?: string;
    weight?: TextWeight;
    type?: TextType;
    heading?: boolean;
    paragraph?: boolean;
    editorial?: boolean;
    underline?: boolean;
    strong?: boolean;
    italic?: boolean;
    formatParams?: FormatParams;
    leftIcon?: React.ComponentType<any>;
    leftIconProps?: Record<string, unknown>;
    rightIcon?: React.ComponentType<any>;
    rightIconProps?: Record<string, unknown>;
    color?: string;
    alignStart?: boolean;
    onPress?: () => void;
    tabIndex?: string;
    ariaHidden?: boolean;
    disabled?: boolean;
};

export function TextButton({
    label,
    text,
    type = 'large',
    heading = false,
    paragraph = false,
    editorial = false,
    underline = true,
    strong = false,
    italic = false,
    formatParams,
    leftIcon,
    leftIconProps = {},
    rightIcon,
    rightIconProps = {},
    color,
    alignStart = false,
    onPress,
    tabIndex,
    ariaHidden = false,
    disabled: ownDisabled = false,
}: TextButtonProps) {
    const formDisabled = useFormDisabled();
    const disabled = formDisabled || ownDisabled;
    const { colors } = useTheme();
    const resolvedColor = color ?? colors.colorTextPrimary;
    const s = TextButtonStyle;
    const [hover, setHover] = useState(false);
    const opacity = hover ? 0.7 : 1;

    const tabIndexValue = React.useMemo(() => {
        const parsed = parseInt(tabIndex ?? '0', 10);
        return parsed === -1 || parsed === 0 ? parsed : undefined;
    }, [tabIndex]);

    // pre-set props once to reduce code duplication and lines of code for this
    // component
    const defaultTextProps = {
        color: resolvedColor,
        formatParams,
        label,
        strong,
        text,
        type,
        underline,
    };

    return (
        <HoverView
            aria-hidden={ariaHidden}
            disabled={disabled}
            onPress={onPress}
            role='button'
            setHover={setHover}
            shrink
            style={[s.button, { opacity }, alignStart ? { alignSelf: 'flex-start' } : null]}
            tabIndex={tabIndexValue}
            testID={makeTestID({ label, text, formatParams })}
        >
            {leftIcon && React.createElement(leftIcon, { ...leftIconProps, color: resolvedColor })}
            {leftIcon && <Pad size={8} />}
            {paragraph ? (
                <Paragraph {...defaultTextProps} />
            ) : heading ? (
                <Heading {...defaultTextProps} weight={strong ? 'medium' : 'regular'} />
            ) : (
                <UtilityText {...defaultTextProps} weight={strong ? 'medium' : 'regular'} />
            )}
            {rightIcon && <Pad size={8} />}
            {rightIcon && React.createElement(rightIcon, { ...rightIconProps, color: resolvedColor })}
        </HoverView>
    );
}
const TextButtonStyle = StyleSheet.create({
    button: {
        flexDirection: 'row',
        alignItems: 'center',
    },
});

export type TagProps = {
    label?: TLabel;
    emoji?: string;
    text?: string;
    type?: string;
    strong?: boolean;
    formatParams?: FormatParams;
    color?: string;
    compact?: boolean;
    dashed?: boolean;
    borderColor?: string;
    overrideTinyTextColor?: boolean;
    textColor?: string;
    maxWidth?: string;
};

export function Tag({
    label,
    emoji,
    text,
    type = 'emphasized',
    strong = false,
    formatParams,
    color,
    compact = false,
    dashed = false,
    borderColor,
    overrideTinyTextColor = false,
    textColor,
    maxWidth = '100%',
}: TagProps) {
    const { colors } = useTheme();
    const s = useTagStyles();
    const resolvedTextColor = textColor ?? colors.colorTextSecondaryOnBanner;
    return (
        <View
            style={[
                s.button,
                type === 'emphasized' && s.emphasized,
                type === 'tiny' && s.tiny,
                color ? { borderColor: borderColor || color, backgroundColor: color } : null,
                compact && { height: 28 },
                dashed && { borderStyle: 'dashed' },
                { maxWidth: maxWidth as DimensionValue },
            ]}
        >
            {emoji && (
                <PadBox right={6}>
                    <UtilityText text={emoji} type='tiny' weight='medium' />
                </PadBox>
            )}
            <UtilityText
                color={type === 'tiny' && !overrideTinyTextColor ? colors.colorTextAccent : resolvedTextColor}
                formatParams={formatParams}
                label={label}
                numberOfLines={1}
                text={text}
                type='tiny'
                weight={strong ? 'strong' : 'medium'}
            />
        </View>
    );
}
const useTagStyles = createThemedStyles((tokens) => ({
    button: {
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderWidth: 1,
        borderRadius: 4,
        height: 32,
        borderColor: tokens.colors.colorBorderPrimary,
    },
    emphasized: {
        borderRadius: 100,
    },
    tiny: {
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 100,
        height: 20,
        backgroundColor: tokens.colors.colorSurfaceBannerInfo,
        borderColor: tokens.colors.colorSurfaceBannerInfo,
    },
}));

export type ReactButtonProps = {
    emoji?: string;
    icon?: React.ComponentType<any>;
    wide?: boolean;
    iconProps?: { color: string };
    viewOnly?: boolean;
    label?: TLabel;
    text?: string;
    count?: number;
    selected?: boolean;
    onPress?: () => void;
};

export function ReactButton({
    emoji,
    icon,
    wide = false,
    iconProps,
    viewOnly = false,
    label,
    text,
    count,
    selected = false,
    onPress,
}: ReactButtonProps) {
    const { colors } = useTheme();
    const s = useReactButtonStyles();
    const resolvedIconProps = iconProps ?? { color: colors.colorTextPrimary };
    const selectedLabel = selected ? t('button.react_button_selected', 'selected') : t('button.react_button_deselected', 'deselected');
    const tSelected = useTranslation(selectedLabel);
    const tLabel = useTranslation(label) ?? text;
    const ariaText = useTranslation(t('button.react_button_aria', '{count} {tLabel} ({tSelected})'), {
        count: count ?? '',
        tLabel,
        tSelected,
    });
    return (
        <HoverView
            ariaText={ariaText ?? undefined}
            disabled={viewOnly}
            hoverStyle={s.hover}
            onPress={onPress}
            pressedStyle={s.pressed}
            role='button'
            style={[s.horiz, !viewOnly && s.button, selected && s.selected, wide && s.wide]}
            testID={makeTestID({ label, text })}
        >
            {icon && React.createElement(icon, resolvedIconProps)}
            {emoji && <UtilityText aria-hidden text={emoji} type='small' weight='medium' />}
            {label || text ? <UtilityText label={label} text={text} type='small' weight='medium' /> : null}
            {count ? <UtilityText color={colors.colorTextWarning} text={count.toString()} type='small' weight='medium' /> : null}
        </HoverView>
    );
}
const useReactButtonStyles = createThemedStyles((tokens) => ({
    horiz: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    viewOnly: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    wide: {
        alignSelf: 'stretch',
        justifyContent: 'center',
    },
    button: {
        flexDirection: 'row',
        alignSelf: 'flex-start',
        height: 32,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: tokens.colors.colorBorderPrimary,
        backgroundColor: tokens.colors.colorSurfacePrimary,
    },
    hover: {
        backgroundColor: translucentHoverColor(tokens.colors.colorSurfacePrimary),
    },
    pressed: {
        backgroundColor: translucentPressColor(tokens.colors.colorSurfacePrimary),
    },
    selected: {
        borderColor: tokens.colors.colorBorderSelected,
        backgroundColor: tokens.colors.colorSurfaceHighlight,
    },
}));

export { FilterButton } from './button/filter-button';

export type BreadCrumbProps = {
    icon?: React.ComponentType<any>;
    ariaLabel?: TLabel;
    testID?: string;
    iconProps?: Record<string, unknown>;
    onPress?: () => void;
};

export function BreadCrumb({ icon, ariaLabel, testID, iconProps = {}, onPress }: BreadCrumbProps) {
    const s = useBreadCrumbStyles();
    return (
        <HoverView
            ariaLabel={ariaLabel}
            hoverStyle={s.hover}
            onPress={onPress}
            pressedStyle={s.pressed}
            role='button'
            style={s.regular}
            testID={testID}
        >
            {icon && React.createElement(icon, iconProps)}
        </HoverView>
    );
}

const useBreadCrumbStyles = createThemedStyles((tokens) => ({
    regular: {
        width: 32,
        height: 32,
        borderRadius: 16,
        justifyContent: 'center',
        alignItems: 'center',
    },
    hover: {
        backgroundColor: toHoverColor(tokens.colors.colorSurfacePrimary),
    },
    pressed: {
        backgroundColor: toPressColor(tokens.colors.colorSurfacePrimary),
    },
}));
